import styled from '@emotion/styled';
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  Skeleton,
  getActionDisplay,
} from '@sunrun/experience-ui-components';
import {
  getEnvironment,
  isSunrunManaged,
} from '@sunrun/sales-experience-shared';
import { useUserProfile } from 'providers/UserProfile';
import { useUrgentTasks } from 'hooks/useUrgentTasks';
import { VerticalWidgetScrollerContainer } from 'components/atoms/VerticalWidgetScrollerContainer';
// eslint-disable-next-line import/no-restricted-paths
import { PIPELINE_CARD_WIDTH } from 'components/organisms/PipelineColumn';
import {
  getCustomerProfileQuery,
  OpenCustomerProfileOptions,
} from 'helpers/listViewHelpers';

function getUrls(): { oneBaseUrl: string } {
  const env = getEnvironment();
  return env === 'production'
    ? { oneBaseUrl: 'https://sunrunone.com' }
    : { oneBaseUrl: 'https://staging.sunrunone.com' };
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function isWithinLast7Days(dateString: string | undefined): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= SEVEN_DAYS_MS;
}

const navigateToCustomerProfile = (
  prospectId: string,
  options?: OpenCustomerProfileOptions,
) => {
  const { oneBaseUrl } = getUrls();
  window.parent.postMessage(
    {
      type: 'COMMAND:CHANGE_URL',
      source: 'Dashboard',
      payload: {
        url: `${oneBaseUrl}/customers/customer-profile/${prospectId}${getCustomerProfileQuery(
          {
            initialTab: 'status',
            openPendingTask: options?.openPendingTask,
          },
        )}`,
      },
    },
    '*',
  );
};

type ActionNeededWidgetProps = {
  onTaskClick?: (
    prospectId: string,
    options?: OpenCustomerProfileOptions,
  ) => void;
  onlyRecentTasks?: boolean;
  isExpanded?: boolean;
  /** When true, the parent column already provides padding, so side padding is adjusted. */
  embeddedInColumn?: boolean;
};

const ActionNeededWidget = ({
  onTaskClick = navigateToCustomerProfile,
  onlyRecentTasks = true,
  isExpanded = false,
  embeddedInColumn = false,
}: ActionNeededWidgetProps) => {
  const { userProfile } = useUserProfile();
  const { urgentTasks, isLoading } = useUrgentTasks(userProfile);

  const visibleTasks = urgentTasks.filter((task) => {
    if (
      task.urgentAction === 'ScheduleInstall' &&
      userProfile &&
      !isSunrunManaged(userProfile)
    ) {
      return false;
    }
    if (task.urgentAction === 'Checkout' && userProfile?.isPartner) {
      return false;
    }
    if (onlyRecentTasks && !isWithinLast7Days(task.statusDate)) {
      return false;
    }
    return true;
  });

  return (
    <ActionNeededWrapper>
      <TaskList isExpanded={isExpanded} embeddedInColumn={embeddedInColumn}>
        {isLoading &&
          [1, 2, 3, 4].map((i) => (
            <StyledSkeleton key={i} embeddedInColumn={embeddedInColumn} />
          ))}
        {!isLoading && visibleTasks.length === 0 && (
          <EmptyMessage>
            <Typography size="small">No actions needed</Typography>
          </EmptyMessage>
        )}
        {!isLoading &&
          visibleTasks.length > 0 &&
          visibleTasks.map((task, index) => {
            const display = getActionDisplay(task);
            if (!display) return null;

            const customerName =
              `${task.firstName} ${task.lastName}`.trim() || 'Customer';

            return (
              <ActionNeededItem
                key={`${task.prospectId}-${index}`}
                isAlert={display.type === 'Alert'}
                embeddedInColumn={embeddedInColumn}>
                <TextBlock>
                  <FlexDiv>
                    <SvgIcon
                      width={18}
                      height={18}
                      name={
                        display.type === 'Alert'
                          ? SvgNames.ErrorFilled
                          : SvgNames.WarningFilled
                      }
                      color={
                        display.type === 'Alert'
                          ? tokens.ERROR_20
                          : tokens.WARNING_20
                      }
                    />
                    <Typography>{customerName}</Typography>
                  </FlexDiv>
                  <Typography size="small">{display.text}</Typography>
                </TextBlock>

                <ChevronButton
                  type="button"
                  onClick={() =>
                    onTaskClick(task.prospectId, {
                      openPendingTask: task.urgentAction === 'PendingTask',
                    })
                  }
                  aria-label={`Review ${customerName}`}>
                  <SvgIcon
                    name={SvgNames.ChevronRight}
                    width={16}
                    height={16}
                    color={tokens.BRAND_HEROBLUE}
                  />
                </ChevronButton>
              </ActionNeededItem>
            );
          })}
      </TaskList>
    </ActionNeededWrapper>
  );
};

const TaskList = styled(VerticalWidgetScrollerContainer)<{
  isExpanded: boolean;
  embeddedInColumn: boolean;
}>`
  /* Higher specificity so this wins over VerticalWidgetScrollerContainer padding.
     Fixed-width cards leave a gap for the scrollbar, matching other pipeline columns. */
  && {
    ${({ embeddedInColumn }) =>
      embeddedInColumn &&
      `
      padding-left: 0;
      padding-right: 0;
    `}
  }

  ${({ isExpanded }) =>
    isExpanded &&
    `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${PIPELINE_CARD_WIDTH}px, 1fr));
    align-content: start;
  `}
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const StyledSkeleton = styled(Skeleton)<{ embeddedInColumn: boolean }>`
  width: ${({ embeddedInColumn }) =>
    embeddedInColumn ? `${PIPELINE_CARD_WIDTH}px` : '100%'};
  height: 74px;
  min-height: 74px;
`;

const EmptyMessage = styled.div`
  padding: 12px 0;
  text-align: center;
`;

const ActionNeededWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ActionNeededItem = styled.div<{
  isAlert: boolean;
  embeddedInColumn: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  gap: 8px;
  box-sizing: border-box;
  width: ${({ embeddedInColumn }) =>
    embeddedInColumn ? `${PIPELINE_CARD_WIDTH}px` : '100%'};
  background-color: ${({ isAlert }) =>
    isAlert ? tokens.CHARGERED_90 : tokens.CHARGEYELLOW_90};
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ChevronButton = styled.button`
  flex-shrink: 0;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export { ActionNeededWidget };
